import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner, Alert, Carousel } from "react-bootstrap";
import { FiClock, FiCalendar } from "react-icons/fi";
import { MdOutlineAccessTimeFilled } from 'react-icons/md';
import "../styles/MagneticGrillCleaningModal.css";

const CleaningHistoryModal = ({
    showModal,
    onHide,
    currentComponent,      // "hopper1", "hopper2", "metal_calibration_history", "coin_metal_calibration_history", "tape_missalignment"
    formatIfTimestamp,     // optional formatting function
    activeIndex = 0,
    setActiveIndex,
    turmericCleaningHistory,
    rtcCleaningHistory,
    packagingCleaningHistory,
    packagingTapeMissAlignData,
    cleaningType = "turmeric"
}) => {
    const [historyData, setHistoryData] = useState([]);
    const [internalActiveIndex, setInternalActiveIndex] = useState(0);

    const currentActiveIndex = setActiveIndex ? activeIndex : internalActiveIndex;
    const handleSetActiveIndex = setActiveIndex ? setActiveIndex : setInternalActiveIndex;
    // console.log("packaging cleaning data -->>> bhushan ", packagingTapeMissAlignData);

    useEffect(() => {
        if (showModal && currentComponent) {
            let sourceData = [];

            if (cleaningType === "turmeric" && turmericCleaningHistory) {
                sourceData = turmericCleaningHistory[currentComponent] || [];
            } else if (cleaningType === "rtc" && rtcCleaningHistory) {
                sourceData = rtcCleaningHistory[currentComponent] || [];
            } else if (cleaningType === "packaging" && packagingCleaningHistory) {
                if (currentComponent === "metal_calibration_history") {
                    sourceData = packagingCleaningHistory.metal_calibration_history || [];
                } else if (currentComponent === "coin_metal_calibration_history") {
                    sourceData = packagingCleaningHistory.coin_metal_calibration_history || [];
                }
            } else if (cleaningType === "tape_missalignment" && packagingTapeMissAlignData) {
                sourceData = packagingTapeMissAlignData || [];
            }
            // console.log("Selected sourceData for", currentComponent, ":", sourceData);
            const mapped = sourceData.map((item, idx) => ({
                id: idx,
                timestamp: item.timestamp,
                cleaning_image: item.file_name,
                shift: item.shift,
                partition_key: item.partition_key
            }));

            setHistoryData(mapped);
            handleSetActiveIndex(0);
        }
    }, [showModal, currentComponent, turmericCleaningHistory, rtcCleaningHistory, packagingCleaningHistory, packagingTapeMissAlignData, cleaningType]);

    const getModalTitle = () => {
        const compNames = {
            hopper1: "Hopper 1",
            hopper2: "Hopper 2",
            premixer1: "Premixer 1",
            premixer2: "Premixer 2",
            finalmixer1: "Final Mixer 1",
            finalmixer2: "Final Mixer 2",
            metal_calibration_history: "Metal Detector",
            coin_metal_calibration_history: "Coin Metal Detector",
            tape_missalignment: "Tape Misalignment" // New entry
        };

        const typeNames = {
            turmeric: "Magnetic Grill",
            rtc: "Magnetic Rod/Plate",
            packaging: "Calibration",
            tape_missalignment: "History" // New entry
        };

        if (cleaningType === "packaging") {
            return `${compNames[currentComponent]} ${typeNames[cleaningType]} History`;
        } else if (cleaningType === "tape_missalignment") {
            return `${compNames[currentComponent]} ${typeNames[cleaningType]}`;
        }

        return `${typeNames[cleaningType]} Cleaning History - ${compNames[currentComponent]}`;
    };

    const getShiftBadgeColor = (shift) => {
        switch (shift) {
            case 'I': return 'bg-primary';
            case 'II': return 'bg-success';
            case 'III': return 'bg-warning';
            default: return 'bg-secondary';
        }
    };

    const formatTimestamp = (timestamp) => {
        const dateObj = new Date(timestamp);

        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const year = dateObj.getFullYear();

        let hours = dateObj.getHours();
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';

        hours = hours % 12;
        hours = hours ? hours : 12; // hour '0' should be '12'

        return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
    };

    const getDisplayText = () => {
        if (cleaningType === "packaging") {
            return "calibration";
        } else if (cleaningType === "tape_missalignment") {
            return "tape misalignment";
        }
        return "cleaning";
    };

    return (
        <Modal
            show={showModal}
            onHide={onHide}
            centered
            size="lg"
            backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title className="fs-5">
                    {getModalTitle()}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {historyData.length === 0 ? (
                    <div className="text-center py-4">
                        <div className="text-muted">
                            <FiClock size={48} className="mb-3" />
                            <p>No {getDisplayText()} history found for this component.</p>
                        </div>
                    </div>
                ) : (
                    <div className="carousel-container">
                        <div className="text-center mb-3">
                            <span className="badge bg-primary me-2">
                                {currentActiveIndex + 1} of {historyData.length}
                            </span>
                            {historyData[currentActiveIndex]?.shift && (
                                <span className={`badge ${getShiftBadgeColor(historyData[currentActiveIndex].shift)}`}>
                                    Shift {historyData[currentActiveIndex].shift}
                                </span>
                            )}
                        </div>

                        <Carousel
                            className="w-full custom-carousel"
                            style={{ margin: "1rem 0" }}
                            interval={null}
                            activeIndex={currentActiveIndex}
                            onSelect={handleSetActiveIndex}
                            indicators
                        >
                            {historyData.map(item => (
                                <Carousel.Item key={item.id}>
                                    <div className="cleaning-history-item border rounded p-3 mx-3">

                                        <div className="d-flex justify-content-center mb-3">
                                            <div className="status-card p-3 border rounded shadow-sm" style={{ width: "60%" }}>
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <div className="me-3">
                                                        <MdOutlineAccessTimeFilled
                                                            style={{ width: "25px", height: "25px", color: "#0d6efd" }}
                                                        />
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="status-title mb-1">
                                                            <strong>
                                                                {cleaningType === "packaging" ? "Calibration" :
                                                                    cleaningType === "tape_missalignment" ? "Tape Misalignment" : "Cleaning"} Date & Time
                                                            </strong>
                                                        </div>
                                                        <div className="text-primary fw-bold">
                                                            {formatIfTimestamp
                                                                ? formatIfTimestamp(item.timestamp)
                                                                : formatTimestamp(item.timestamp)
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-center">
                                            {item.cleaning_image ? (
                                                <div
                                                    style={{
                                                        width: "80%",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <img
                                                        src={item.cleaning_image}
                                                        alt={`${cleaningType === "packaging" ? "Calibration" :
                                                            cleaningType === "tape_missalignment" ? "Tape Misalignment" : "Cleaning"} ${item.timestamp}`}
                                                        className="img-fluid rounded shadow"
                                                        style={{
                                                            maxHeight: '300px',
                                                            maxWidth: '100%',
                                                            cursor: 'pointer',
                                                            border: '2px solid #dee2e6'
                                                        }}
                                                        onClick={() => window.open(item.cleaning_image, '_blank')}
                                                    />
                                                    <div className="mt-2 text-muted">
                                                        <FiCalendar className="me-1" />
                                                        <small>Click to view full image</small>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center text-muted p-5">
                                                    <FiClock size={48} className="mb-3 opacity-50" />
                                                    <p>No image available for this {getDisplayText()} record</p>
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </Carousel.Item>
                            ))}
                        </Carousel>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CleaningHistoryModal;